import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { IHelpGuideSummary } from '@apis/help';
import { useScrollSpy } from './useScrollSpy';
import styles from './GuideReaderPage.module.css';

interface IGuideTocProps {
  slug: string;
  headings: { id: string; text: string; level: 2 | 3 }[];
  /** Shown instead of headings when the guide has none and belongs to a parent. */
  siblings: { parent: IHelpGuideSummary; list: IHelpGuideSummary[] } | null;
}

export function GuideToc({ slug, headings, siblings }: IGuideTocProps) {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const active = useScrollSpy(slug, headings.map((heading) => heading.id), hash ? hash.slice(1) : null);
  if (headings.length === 0 && siblings) {
    const label = t('help.otherReports', { parent: siblings.parent.title });
    return (
      <nav className={styles.toc} aria-label={label}>
        <h4>{label}</h4>
        {siblings.list.map((sibling) => (
          <Link key={sibling.slug} to={`/help/${sibling.slug}`} className={sibling.slug === slug ? styles.tocOn : undefined}>{sibling.title}</Link>
        ))}
      </nav>
    );
  }
  if (headings.length === 0) return null;
  return (
    <nav className={styles.toc} aria-label={t('help.onThisPage')}>
      <h4>{t('help.onThisPage')}</h4>
      {headings.map((heading) => (
        <Link key={heading.id} to={`/help/${slug}#${heading.id}`} className={`${heading.level === 3 ? styles.tocL3 : ''} ${active === heading.id ? styles.tocOn : ''}`}>
          {heading.text}
        </Link>
      ))}
    </nav>
  );
}
